<template>
  <div class="default-select" :class="component_class_list">
    <span v-if="display_placeholder" class="default-select_label">
      <p class="default-select_label_p">
        {{ placeholder }}
        <span
            v-if="required"
            class="default-select_label-required">*</span>
        <span
            v-if="!is_valid"
            class="default-input__label-error">
          ({{ $t(error_text) }})
        </span>
      </p>
    </span>
    <div class="default-select_field"
         v-blink_on_create="blink_select"
         :class="{opened, multiple}"
         @click="changeOpen(!opened)">
      <div class="default-select_field-value">
        <template v-if="!has_selected_value">{{empty_selected_text}}</template>
        <template v-else-if="!selected_val_is_array">{{ get_selected_val }}</template>
        <template v-else>
          <div v-for="item in get_selected_val_arr"
               class="default-select_field-value__item">
            {{ item[display_item_api_key] || item }}
            <span @click.stop="deselectArrayValue(item)">✖</span>
          </div>
        </template>
      </div>
      <span class="default-select_field-deselect"
            v-if="get_allow_deselect"
            @click.stop="deselectValue">✖</span>
      <span class="default-select_field-arrow">
          <span class="default-select_field-arrow__sign" :class="{rotate: opened}"></span>
      </span>
    </div>
    <div class="default-select_dropdown"
         :style="get_dropdown_styles"
         :class="search_dropdown_classes">
      <div v-if="searchable" class="default-select_dropdown__search">
        <search-input
          ref="search_input"
          type="text"
          class="w-100"
          :value="search_val"
          @input="changeSearch"
        />
      </div>
      <div
        ref="dropdown_scroll"
        class="default-select_dropdown__list">
        <match-text
          v-for="(item, index) in list_to_show"
          class="default-select_dropdown__item"
          :match_val="search_val"
          :name="item[display_item_api_key] || item"
          :class="{hovered: index + 1 === focus_on}"
          :prop_id="item.id"
          :key="`${uniq_id}_${item.id}_${index}`"
          @click="selectedValue(item)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import MatchText from "./MatchText";
import SearchInput from "./SearchInput";
import typing_mixin from "../../mixins/typing_mixin";

const CancelToken = axios.CancelToken;
let axiosSource;

export default {
  name: "DefaultSelect",
  mixins:[typing_mixin],
  components: {
    SearchInput,
    MatchText
  },
  props: {
    selected_val: null,
    placeholder: {
      type: String,
      default: ''
    },
    display_placeholder: {
      type: Boolean,
      default: true
    },
    // to show name from response list
    display_item_api_key: {
      type: String,
      default: 'title'
    },
    list_outer: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: false
    },
    allow_deselect: {
      type: Boolean,
      default: false
    },
    axios_request: {
      type: Function,
      default: null
    },
    axios_data: {
      type: Object,
      default: () => {}
    },
    axios_request_paginated: {
      type: Boolean,
      default: false
    },
    fetch_on_search: {
      type: Boolean,
      default: false
    },
    keep_open_on_select: {
      type: Boolean,
      default: false
    },
    empty_selected_text: {
      type: String,
      default: 'Choose value'
    },
    required: {
      type: Boolean,
      default: false
    },
    error_text: {
      type: String,
      default: 'errors.this_field_is_required'
    },
    blink_select: {
      type: Boolean,
      default: false
    },
  },
  computed: {
    is_valid() {
      return this.valid
    },
    component_class_list() {
      return {
        validate_select: this.required,
        error: !this.valid,
      }
    },
    get_list() {
      return this.list_outer.length ? this.list_outer : this.list
    },
    get_allow_deselect() {
      if (!this.allow_deselect) return false
      return this.has_selected_value && !this.multiple
    },
    has_selected_value() {
      return Boolean(
        (this.selected_val_is_array && this.selected_val.length)
        || (!this.selected_val_is_array && this.selected_val)
      )
    },
    get_selected_val() {
      return this.selected_val[this.display_item_api_key] || this.selected_val
    },
    get_selected_val_arr() {
      if (!this.multiple || !this.selected_val_is_array) return []
      return this.selected_val
    },
    selected_val_is_array() {
      return Array.isArray(this.selected_val)
    },
    lower_case_search() {
      return this.search_val.toLowerCase()
    },
    list_of_objects(){
      if (!this.get_list.length) return false
      return typeof this.get_list[0] === 'object'
    },
    list_to_show() {
      if (this.list_of_objects) {
        return this.get_list.filter(el => {
          return `${el[this.display_item_api_key]}`.toLowerCase().includes(this.lower_case_search)
        })
      }
      return this.get_list.filter(el => el.includes(this.lower_case_search))
    },
    search_dropdown_classes() {
      return {
        opened: this.opened,
      }
    },
    get_dropdown_styles() {
      return {
        top: `${this.dropdown_styles.top}px`,
        left: `${this.dropdown_styles.left}px`,
        width: `${this.dropdown_styles.width}px`,
      }
    },
    uniq_id() {
      return this._uid
    },
  },
  data() {
    return {
      opened: false,
      search_val: '',
      list: [],
      focus_on: 0,
      first_fetch: true,
      current_pagination_page: 1,
      last_pagination_page: 1,
      data_loading: false,

      valid: true,

      dropdown_styles: {
        top: 0,
        left: 0,
        width: 0
      }
    }
  },
  mounted() {
    if (this.axios_request_paginated) {
      this.$refs.dropdown_scroll.addEventListener('scroll', this.dropdownScrollEvent)
    }
  },
  methods: {
    changeOpen(bool) {
      if (bool) {
        this.openDropdown()
      } else {
        this.closeDropdown()
      }
    },
    openDropdown() {
      this.scrollEvent()
      window.addEventListener('scroll', this.scrollEvent)
      this.opened = true
      setTimeout(() => {
        this.focusOnSearchInput()
        this.getAxiosRequest()
        document.addEventListener('click', this.clickOutsideEvent)
      }, 0)
    },
    focusOnSearchInput() {
      this.$refs.search_input?.focus()
    },
    closeDropdown() {
      this.opened = false
      window.removeEventListener('scroll', this.scrollEvent)
      this.removeEventListener()
    },
    changeSearch(value) {
      this.search_val = value
      if (!this.fetch_on_search) return
      this.typing(this.searchCallback)
    },
    searchCallback() {
      this.current_pagination_page = 1
      this.getAxiosRequest()
    },
    deselectValue() {
      this.emitData(this.multiple ? [] : null)
    },
    deselectArrayValue(item) {
      if (!this.selected_val_is_array) return
      this.emitData(
        this.list_of_objects
          ? this.selected_val.filter(el => el.id !== item.id)
          : this.selected_val.filter(el => el !== item)
      )
    },
    getAxiosRequest(push_to_list = false) {
      if (!this.axios_request || this.current_pagination_page > this.last_pagination_page) return;
      if (axiosSource) {
        axiosSource.cancel('Operation canceled by the user');
      }
      axiosSource = CancelToken.source()
      this.data_loading = true
      if (!push_to_list) {
        this.list = []
      }
      this.axios_request(
        {
          ...this.axios_data,
          ...{
            search: this.search_val,
            page: this.current_pagination_page
          }
        }, axiosSource.token)
        .then(({data}) => {
          (data.collection || data || []).map(el => {
            this.list.push(el)
          })
          this.current_pagination_page = data.current_page || 1
          this.last_pagination_page = data.last_page || 1

        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          this.data_loading = false
        })
    },
    selectedValue(item) {
      let emit_val = item
      if (this.multiple) {
        let selected_val = []
        if (this.has_selected_value) {
          selected_val = [...JSON.parse(JSON.stringify(this.selected_val))]
        }
        if (typeof item !== 'object') {
          item = item.trim()
          selected_val = selected_val.map(el => el.trim());
        }
        if (
          (typeof item === 'object' && !selected_val.some(el => el.id === item.id))
          || (typeof item !== 'object' && !selected_val.some(el => el === item))
        ) {
          selected_val.push(item)
        }
        emit_val = selected_val
      }
      this.emitData(emit_val)
    },
    emitData(emit_val) {
      this.$emit('change', emit_val)
      if (!this.keep_open_on_select) {
        this.closeDropdown()
      }
    },
    //events
    clickOutsideEvent(e) {
      let closest = e.target.closest('.' + this.$el.className.split(' ').join('.'))
      if (!closest || closest !== this.$el) {
        this.closeDropdown()
      }
    },
    dropdownScrollEvent({target}) {
      if (this.data_loading || target.scrollHeight >= target.scrollHeight + target.scrollTop) return
      this.current_pagination_page++
      this.getAxiosRequest(true)
    },
    removeEventListener() {
      document.removeEventListener('click', this.clickOutsideEvent)
      this.$refs.dropdown_scroll.removeEventListener('click', this.dropdownScrollEvent)
    },
    scrollEvent() {
      let rect = this.$el.getBoundingClientRect()
      if (!rect) return
      this.dropdown_styles.top = rect.bottom
      this.dropdown_styles.left = rect.left
      this.dropdown_styles.width = rect.width
    },
    updateValid() {
      if (!this.required) return
      console.log(this.has_selected_value);
      this.valid = this.has_selected_value
    },
  },
  beforeDestroy() {
    this.removeEventListener()
    window.removeEventListener('scroll', this.scrollEvent)
  },
}
</script>
